import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { adminLogin, getAdminToken, setAdminToken } from "@/lib/api";
import { Loader2, ArrowRight, Lock } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  if (getAdminToken()) {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await adminLogin(email.trim().toLowerCase(), password);
      setAdminToken(data.token);
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0B0A4A] text-[#F4ECFF] font-sans flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-10">
          <img
            src="https://customer-assets.emergentagent.com/job_feminineways-notify/artifacts/vvjjpevv_FeminineWays%20logo.png"
            alt="Feminine Ways"
            className="w-12 h-12 rounded-xl shadow-lg"
          />
          <span className="font-serif text-2xl">
            Feminine<span className="text-[#B87CFF]">Ways</span>
          </span>
        </div>

        <div
          className="rounded-3xl border border-[#F4ECFF]/15 bg-[#F4ECFF]/5 backdrop-blur-xl p-8 sm:p-10"
          data-testid="admin-login-card"
        >
          <div className="flex items-center gap-2 text-[#B87CFF] mb-2">
            <Lock className="w-4 h-4" strokeWidth={1.75} />
            <span className="text-[11px] uppercase tracking-[0.22em] font-medium">Admin access</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#F4ECFF] leading-tight">
            Sign in to manage the waitlist.
          </h1>

          {error && (
            <div
              className="mt-6 rounded-2xl bg-red-500/15 border border-red-400/30 text-red-200 px-4 py-3 text-sm"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#F4ECFF]/70">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@feminineways.app"
                disabled={loading}
                className="mt-2 w-full rounded-full bg-white/5 border border-white/15 text-[#F4ECFF] placeholder-[#F4ECFF]/40 px-5 py-3 outline-none focus:ring-2 focus:ring-[#B87CFF]/40"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#F4ECFF]/70">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={loading}
                className="mt-2 w-full rounded-full bg-white/5 border border-white/15 text-[#F4ECFF] placeholder-[#F4ECFF]/40 px-5 py-3 outline-none focus:ring-2 focus:ring-[#B87CFF]/40"
                data-testid="admin-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#B87CFF] hover:bg-[#F4ECFF] text-[#0B0A4A] disabled:opacity-70 px-6 py-3.5 text-sm font-medium tracking-wide transition-colors"
              data-testid="admin-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  Sign in <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-[#F4ECFF]/45 mt-6">
          A Charlotte Bryan app · charlottebryan.com.au
        </p>
      </div>
    </main>
  );
}
